import { useTranslation } from 'react-i18next'

import { EMPTY_FUNCTION, EMPTY_STRING } from '../../../constants/common'
import { useKeyboard } from '../../../hooks'
import { isHTMLElementFocused } from '../../../utils/html'
import { InputField } from '../../Input'
import { EMPTY_SEARCH_VALUE, SEARCH_TEXT_MAX_LENGTH } from '../Search.constants'
import { setSearchValueText } from '../Search.utils'
import { SearchButton } from '../SearchButton'

import * as styles from './SearchTextInput.module.css'

/**
 * @import {SearchButtonProps} from "../SearchButton/SearchButton"
 */

/**
 * @typedef {object} SearchTextInputProps
 *
 * @property {React.ComponentType<SearchButtonProps>} [Button]
 *
 * @property {SearchButtonProps} [buttonProps]
 *
 * @property {string} [className]
 *
 * @property {number} [maxLength]
 *
 * @property {number} [placeholder]
 *
 * @property {SearchValue} [value]
 *
 * @property {() => void} [onBlur]
 *
 * @property {(search: SearchValue) => void} [onChange]
 *
 * @property {() => void} [onFocus]
 *
 * @property {(search: SearchValue) => void} [onSearch]
 */

/**
 * @param {SearchTextInputProps} props
 */
function SearchTextInput({
  Button = SearchButton,
  buttonProps,
  className = styles.SearchTextInput,
  maxLength = SEARCH_TEXT_MAX_LENGTH,
  placeholder = 'Header.SearchInputPlaceholder',
  ref,
  value = EMPTY_SEARCH_VALUE,
  onBlur = EMPTY_FUNCTION,
  onChange = EMPTY_FUNCTION,
  onFocus = EMPTY_FUNCTION,
  onSearch = EMPTY_FUNCTION,
  ...inputFieldProps
}) {
  const { t } = useTranslation()

  function handleChange(event) {
    const nextValue = setSearchValueText(value, event.target.value)

    if (nextValue !== value) {
      onChange(nextValue)
    }
  }

  function handleClear() {
    const nextValue = setSearchValueText(value, EMPTY_STRING)

    if (nextValue !== value) {
      onChange(nextValue)
    }
  }

  function handleKeyboardEnter() {
    if (isHTMLElementFocused(ref)) {
      onSearch(value)
    }
  }

  useKeyboard(handleKeyboardEnter)

  return (
    <InputField
      {...inputFieldProps}
      afterButton={
        <Button {...buttonProps} search={value} onSearch={onSearch} />
      }
      className={className}
      maxLength={maxLength}
      placeholder={t(placeholder)}
      ref={ref}
      showError={false}
      value={value.text}
      onBlur={onBlur}
      onChange={handleChange}
      onClear={handleClear}
      onFocus={onFocus}
    />
  )
}

export default SearchTextInput
